import { z } from 'zod'
import { feetToPixels, pixelsToFeet } from '../constants/scale.js'
import type { Primitive } from '../dto/primitive.dto.js'
import type { FurnitureClass } from '../enums/furniture-class.enum.js'
import {
  feetToHundredths,
  formatFeet,
  hundredthsToFeet,
  hundredthsToFeetOrNull,
  isHundredthsFeet,
  parseFeetInput,
} from './feet-hundredths.js'

/*
 * A riser is not drawn. The catalog stores one unit — a deck so wide and so
 * long — and a placed riser is a runway of whole units laid end to end along
 * its length, with a seam drawn where one unit meets the next. The venue
 * stocks units, so a six-unit runway uses six of them (see `stockUnits`).
 *
 * Runways only, for now: one unit wide, however many long. A stage several
 * decks wide is a different shape and has not been asked for.
 */

/** The largest side one riser unit may be given, in feet — the same cap a dance floor has. */
export const RISER_MAX_FEET = 100

/**
 * The most units one runway may be.
 *
 * A cap on the plan's number, so a typo cannot lay a runway out of the
 * building and claim a warehouse of stock. Fifty eight-foot decks is four
 * hundred feet, past any room this app draws.
 */
export const RISER_MAX_UNITS = 50

/** A unit-size field: positive, at most two decimal places, and within the cap. */
export const riserFeetSchema = z
  .number()
  .positive('Enter a size greater than 0.')
  .max(RISER_MAX_FEET, `Size cannot exceed ${String(RISER_MAX_FEET)} ft.`)
  .refine(isHundredthsFeet, 'Use at most two decimal places.')

/** Why `widthFeet` × `lengthFeet` is not a riser unit, or null when it is. */
export function riserSpecError(widthFeet: number | null, lengthFeet: number | null): string | null {
  if (widthFeet === null || lengthFeet === null) {
    return 'A riser needs the width and length of one unit.'
  }
  if (!isHundredthsFeet(widthFeet) || !isHundredthsFeet(lengthFeet)) {
    return 'Use at most two decimal places.'
  }
  if (widthFeet <= 0 || lengthFeet <= 0) {
    return 'Width and length must be greater than 0.'
  }
  if (widthFeet > RISER_MAX_FEET || lengthFeet > RISER_MAX_FEET) {
    return `Width and length cannot exceed ${String(RISER_MAX_FEET)} ft.`
  }
  return null
}

/** The two numbers a riser stores, or neither. Anything else stores neither. */
export interface RiserSpec {
  widthFeet: number | null
  lengthFeet: number | null
}

/**
 * The spec to store for a piece that will be `furnitureClass`.
 *
 * Anything that is not a riser stores neither number. A riser stores both, or
 * the write is refused — a riser without a unit has no runway to lay out.
 */
export function settleRiser(
  furnitureClass: FurnitureClass,
  widthFeet: number | null,
  lengthFeet: number | null,
): { ok: true; spec: RiserSpec } | { ok: false; message: string } {
  if (furnitureClass !== 'Riser') {
    return { ok: true, spec: { widthFeet: null, lengthFeet: null } }
  }
  const message = riserSpecError(widthFeet, lengthFeet)
  if (message !== null || widthFeet === null || lengthFeet === null) {
    return { ok: false, message: message ?? 'A riser needs the width and length of one unit.' }
  }
  return {
    ok: true,
    spec: {
      widthFeet: hundredthsToFeet(feetToHundredths(widthFeet)),
      lengthFeet: hundredthsToFeet(feetToHundredths(lengthFeet)),
    },
  }
}

/**
 * What an update schema can refuse without the stored row — the same rule a
 * dance floor's update follows. A pair that is not a unit is refused, and so
 * is clearing either number while the class is Riser. One number on its own is
 * left to the service, which knows the stored other half.
 */
export function riserUpdateIssue(
  furnitureClass: FurnitureClass | undefined,
  widthFeet: number | null | undefined,
  lengthFeet: number | null | undefined,
): string | null {
  if (furnitureClass === 'Riser' && (widthFeet === null || lengthFeet === null)) {
    const cleared = settleRiser('Riser', null, null)
    return cleared.ok ? null : cleared.message
  }
  if (typeof widthFeet === 'number' && typeof lengthFeet === 'number') {
    const pair = settleRiser('Riser', widthFeet, lengthFeet)
    return pair.ok ? null : pair.message
  }
  return null
}

/**
 * What the furniture forms show, and what they send. Blank is not an error
 * yet; anything that is not a riser sends neither number.
 */
export function riserFormValues(
  furnitureClass: FurnitureClass,
  widthText: string,
  lengthText: string,
): { widthFeet: number | null; lengthFeet: number | null; error: string | null } {
  if (furnitureClass !== 'Riser') {
    return { widthFeet: null, lengthFeet: null, error: null }
  }
  const widthFeet = parseFeetInput(widthText)
  const lengthFeet = parseFeetInput(lengthText)
  return {
    widthFeet,
    lengthFeet,
    error: widthFeet !== null && lengthFeet !== null ? riserSpecError(widthFeet, lengthFeet) : null,
  }
}

/** A runway's unit count: a whole number from one to the cap. */
function clampRiserUnits(units: number): number {
  if (!Number.isFinite(units)) return 1
  return Math.min(RISER_MAX_UNITS, Math.max(1, Math.round(units)))
}

/** Seams are drawn at half the outline's weight, so the runway reads as one piece first. */
const SEAM_STROKE_WIDTH = 0.75

/**
 * The runway a riser is: `units` units end to end along the x axis, centered
 * on the piece's anchor like every other primitive, with a seam across the
 * runway at each joint.
 *
 * The outline keeps its id and paint, so changing the count does not clear a
 * fill or look like a different shape to anything that keyed on the id.
 */
export function riserPrimitives(
  widthFeet: number,
  lengthFeet: number,
  units: number,
  existing: readonly Primitive[] = [],
): Primitive[] {
  const outline = existing.find((primitive) => primitive.shape === 'rect')
  const count = clampRiserUnits(units)
  const unitLength = feetToPixels(hundredthsToFeet(feetToHundredths(lengthFeet)))
  const width = feetToPixels(hundredthsToFeet(feetToHundredths(widthFeet)))
  const total = unitLength * count
  const rectId = outline?.id ?? 'riser'
  const runway: Primitive = {
    id: rectId,
    shape: 'rect',
    x: 0,
    y: 0,
    rotation: 0,
    width: total,
    height: width,
    ...(outline?.shape === 'rect' && outline.fill !== undefined ? { fill: outline.fill } : {}),
    ...(outline?.shape === 'rect' && outline.stroke !== undefined ? { stroke: outline.stroke } : {}),
    ...(outline?.shape === 'rect' && outline.strokeWidth !== undefined ? { strokeWidth: outline.strokeWidth } : {}),
    ...(outline?.shape === 'rect' && outline.cornerRadius !== undefined ? { cornerRadius: outline.cornerRadius } : {}),
  }
  const seams: Primitive[] = Array.from({ length: count - 1 }, (_, i) => ({
    id: `${rectId}-seam-${String(i + 1)}`,
    shape: 'line',
    x: -total / 2 + unitLength * (i + 1),
    y: 0,
    rotation: 0,
    points: [0, -width / 2, 0, width / 2],
    strokeWidth: SEAM_STROKE_WIDTH,
  }))
  return [runway, ...seams]
}

/** The runway's length along its units, in plan pixels, or null when it has no outline. */
function runwayLength(primitives: readonly Primitive[]): number | null {
  const outline = primitives.find((primitive) => primitive.shape === 'rect')
  return outline?.shape === 'rect' ? outline.width : null
}

/** The feet a drawn length stands for, to the hundredth the catalog stores. */
function drawnFeet(pixels: number): number {
  return hundredthsToFeet(feetToHundredths(pixelsToFeet(pixels)))
}

/**
 * One unit of a placed runway as it is drawn, in feet, or null when it has no
 * outline to read.
 *
 * A runway keeps the unit it was laid out of. Editing the catalog's unit size
 * changes the risers placed after it, not the runways already on plans — so
 * what the Selection panel says about a runway, and the unit a count change
 * lays again, come from the drawing rather than the catalog.
 */
export function drawnRiserUnit(piece: {
  units?: number | undefined
  primitives: readonly Primitive[]
}): { widthFeet: number; lengthFeet: number } | null {
  const outline = piece.primitives.find((primitive) => primitive.shape === 'rect')
  if (outline?.shape !== 'rect') return null
  const widthFeet = drawnFeet(outline.height)
  const lengthFeet = drawnFeet(outline.width / clampRiserUnits(piece.units ?? 1))
  return widthFeet > 0 && lengthFeet > 0 ? { widthFeet, lengthFeet } : null
}

/** A placed piece, as far as resizing a runway needs one. */
export interface RiserPlacement {
  x: number
  y: number
  rotation: number
  primitives: readonly Primitive[]
}

/**
 * The same runway at `units` units, its start end left where it was.
 *
 * The runway is centered on its anchor, so a new length alone would grow it at
 * both ends — and a runway laid against the stage would come away from it.
 * The anchor moves by half the change along the runway, turned as the piece is
 * turned, so only the far end moves.
 */
export function resizeRiser(
  piece: RiserPlacement,
  widthFeet: number,
  lengthFeet: number,
  units: number,
): { x: number; y: number; units: number; primitives: Primitive[] } {
  const count = clampRiserUnits(units)
  const primitives = riserPrimitives(widthFeet, lengthFeet, count, piece.primitives)
  const before = runwayLength(piece.primitives)
  const after = runwayLength(primitives) ?? 0
  const shift = before === null ? 0 : (after - before) / 2
  const rad = (piece.rotation * Math.PI) / 180
  return {
    x: piece.x + shift * Math.cos(rad),
    y: piece.y + shift * Math.sin(rad),
    units: count,
    primitives,
  }
}

/** "4 × 24 ft": the runway's width by its length at `units` units. */
export function formatRiserSize(widthFeet: number, lengthFeet: number, units: number): string {
  const length = hundredthsToFeet(feetToHundredths(lengthFeet) * clampRiserUnits(units))
  return `${formatFeet(widthFeet)} × ${formatFeet(length)} ft`
}

/** The columns to write, in hundredths of a foot, and the one-unit runway when it changes. */
export interface RiserWrite {
  widthHundredths: number | null
  lengthHundredths: number | null
  primitives?: Primitive[]
}

/** The row an update is changing. Hundredths, because that is how the row stores them. */
export interface RiserStored {
  existingClass: FurnitureClass
  existingWidthHundredths: number | null
  existingLengthHundredths: number | null
  existingPrimitives: readonly Primitive[]
}

/**
 * The fields an update sent. A missing field is left as stored, null is an
 * explicit clear, and a drawing that arrives for a riser is replaced by its
 * runway.
 */
export interface RiserPatch {
  class?: FurnitureClass
  widthFeet?: number | null
  lengthFeet?: number | null
  primitives?: readonly Primitive[]
}

function columnsFrom(spec: RiserSpec, existingPrimitives: readonly Primitive[], regenerate: boolean): RiserWrite {
  const write: RiserWrite = {
    widthHundredths: spec.widthFeet === null ? null : feetToHundredths(spec.widthFeet),
    lengthHundredths: spec.lengthFeet === null ? null : feetToHundredths(spec.lengthFeet),
  }
  if (regenerate && spec.widthFeet !== null && spec.lengthFeet !== null) {
    write.primitives = riserPrimitives(spec.widthFeet, spec.lengthFeet, 1, existingPrimitives)
  }
  return write
}

/**
 * The columns a create stores. A riser's catalog shape is one unit, replacing
 * whatever drawing arrived with it; anything else stores neither number.
 */
export function riserCreate(
  furnitureClass: FurnitureClass,
  widthFeet: number | null,
  lengthFeet: number | null,
): { ok: true; write: RiserWrite } | { ok: false; message: string } {
  const settled = settleRiser(furnitureClass, widthFeet, lengthFeet)
  if (!settled.ok) return settled
  return { ok: true, write: columnsFrom(settled.spec, [], furnitureClass === 'Riser') }
}

/**
 * The columns an update stores, or null when this patch does not mention a
 * riser and the columns should be left alone. The one-unit shape is rewritten
 * when the class becomes Riser, either size changes, or a drawing arrives for
 * a riser. Runways already on plans keep the size they were laid out at.
 */
export function riserUpdate(
  stored: RiserStored,
  patch: RiserPatch,
): { ok: true; write: RiserWrite | null } | { ok: false; message: string } {
  const nextClass = patch.class ?? stored.existingClass
  const mentionsRiser =
    patch.class !== undefined ||
    patch.widthFeet !== undefined ||
    patch.lengthFeet !== undefined ||
    (patch.primitives !== undefined && nextClass === 'Riser')
  if (!mentionsRiser) return { ok: true, write: null }

  const widthFeet = patch.widthFeet !== undefined ? patch.widthFeet : hundredthsToFeetOrNull(stored.existingWidthHundredths)
  const lengthFeet =
    patch.lengthFeet !== undefined ? patch.lengthFeet : hundredthsToFeetOrNull(stored.existingLengthHundredths)
  const settled = settleRiser(nextClass, widthFeet, lengthFeet)
  if (!settled.ok) return settled
  const classChanged = patch.class !== undefined && patch.class !== stored.existingClass
  const regenerate =
    nextClass === 'Riser' &&
    (classChanged || patch.widthFeet !== undefined || patch.lengthFeet !== undefined || patch.primitives !== undefined)
  return { ok: true, write: columnsFrom(settled.spec, stored.existingPrimitives, regenerate) }
}
